import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { EditableSection } from './EditableSection.js';
import { SpecTable } from './SpecTable.js';
import type { SpecIntegrationTest } from '../../../lib/api/spec-requirements.js';

export interface IntegrationTestSectionProps {
  /** 開いている案件 */
  pjKey: string;
  /** 今保存されている総合テスト。無い案件では null */
  integrationTest: SpecIntegrationTest | null;
  /** レビュー操作。呼び出し側が組み立てるので、ここでは `ReviewActions` を知らない */
  actions: React.ReactNode;
}

/** シナリオ 1 つ分にまとめた行。 @graph-connects none */
interface ScenarioSummary {
  scene: string;
  scenario: string;
  screens: string[];
  steps: number;
  covers: string[];
}

/**
 * 「業務フロー」の列をノードに分ける。区切り文字は `packages/domain/spec-flow` の
 * `parseCovers`（保存時の突合ロジック）に合わせ、全角カンマ・空白も許容する。
 * @graph-connects none
 */
function nodesOf(covers: string): string[] {
  return covers
    .split(/[,、\s]+/)
    .map((c) => c.trim())
    .filter((c) => c !== '');
}

/** 同じものを足さずに末尾へ積む。 @graph-connects none */
function pushNew(into: string[], values: string[]): void {
  for (const value of values) {
    if (!into.includes(value)) into.push(value);
  }
}

/**
 * 行をシナリオ単位にまとめる。**同じシナリオ名が続く行が 1 つのシナリオの手順**なので、
 * 続いている間だけ 1 つに畳む（離れて同じ名前が出てきたら別のシナリオとして扱う）。
 * @graph-connects none
 */
export function toScenarios(rows: SpecIntegrationTest['rows']): ScenarioSummary[] {
  const summaries: ScenarioSummary[] = [];
  for (const row of rows) {
    const last = summaries[summaries.length - 1];
    const sameScenario =
      last !== undefined && last.scenario === row.scenario && last.scene === row.scene;
    if (sameScenario) {
      last.steps += 1;
      pushNew(last.screens, [row.screen]);
      pushNew(last.covers, nodesOf(row.covers));
      continue;
    }
    summaries.push({
      scene: row.scene,
      scenario: row.scenario,
      screens: [row.screen],
      steps: 1,
      covers: nodesOf(row.covers),
    });
  }
  return summaries;
}

/**
 * 総合テストの節。**シナリオの一覧だけを出し、詳細と編集は専用ページに送る。**
 *
 * 列が 7 つあり、事前条件は期待する結果が 1 つに決まるところまで書くので長い。この幅で全部を
 * 読み書きするのは成り立たないので、ここでは「どんなシナリオが何 step あるか」までにとどめる。
 *
 * **専用ページへのリンクは見出しの右、レビュー操作は本文の末尾**に置く。どちらも消さずに並べる
 * ため（他の 4 つの節もレビュー操作は本文の末尾なので、位置が節ごとにぶれない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 総合テストのシナリオ一覧を出し、詳細と編集の専用ページへ送り、レビュー操作を並べる節
 * @graph-connects toScenarios [calls] シナリオ単位へのまとめ
 */
export function IntegrationTestSection({
  pjKey,
  integrationTest,
  actions,
}: IntegrationTestSectionProps) {
  const { t } = useTranslation();
  const scenarios = integrationTest === null ? [] : toScenarios(integrationTest.rows);
  // まだ無い成果物は「書く」、あるものは専用ページを「開く」
  const actionKey = integrationTest === null ? 'spec.action.write' : 'spec.action.open';

  return (
    <EditableSection
      heading={t('spec.artifact.integrationTest')}
      action={t(actionKey)}
      editing={false}
      warnings={[]}
      headerAction={
        <Link
          to="/projects/spec/$pjKey/integration-test"
          params={{ pjKey }}
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)]"
          aria-label={t('spec.section.editAria', {
            heading: t('spec.artifact.integrationTest'),
            action: t(actionKey),
          })}
        >
          {t(actionKey)}
        </Link>
      }
    >
      {integrationTest === null ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          {t('spec.integrationTest.empty')}
        </p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文の行が返る。空で隠されていないので、
            読んでいるものが原文だと分かるように断る */}
          {integrationTest?.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.integrationTest.untranslated')}
            </p>
          ) : null}
          <SpecTable
            headings={[
              t('spec.integrationTest.columns.scene'),
              t('spec.integrationTest.columns.scenario'),
              t('spec.integrationTest.columns.screen'),
              t('spec.integrationTest.stepsColumn'),
              t('spec.integrationTest.columns.covers'),
            ]}
            rows={scenarios.map((s, index) => ({
              key: `${String(index)}/${s.scenario}`,
              cells: [
                s.scene,
                s.scenario,
                s.screens.join(' → '),
                t('spec.integrationTest.stepCount', { n: s.steps }),
                s.covers.join(','),
              ],
            }))}
          />
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('spec.integrationTest.detailHint')}
          </p>
        </>
      )}
      {actions}
    </EditableSection>
  );
}
